// P3 cash and capital (CSH-C-04): reads from the cash views, writes through the owner's cash operations
// (GEN-11, CAP-01..04, ADR-093, ADR-095, ADR-096). Amounts arrive as numeric strings and are only displayed.
import { Refusal } from './auth';
import { sendCommand } from './commands';
import { supabase } from './supabase';

export interface BusinessCash {
  business_cash: string;
  garage: string;
  bank: string;
  rounds: string;
  total_contributions: string;
  total_withdrawals: string;
  net_capital: string;
  opening_open: boolean;
}
export interface CashPlace {
  account_id: string;
  kind: 'garage' | 'bank' | 'round';
  name: string;
  round_id: string | null;
  balance: string;
}
export interface CapitalMove {
  id: number;
  kind: 'opening' | 'contribution' | 'withdrawal' | 'bank_transfer';
  business_date: string;
  at: string;
  party: string | null;
  reason: string | null;
  reference: string | null;
  note: string | null;
  actor_name: string | null;
  amount: string;
  direction: 'to_bank' | 'to_garage' | null;
  places: Partial<Record<'garage' | 'bank', string>> | null;
}
export type Direction = 'to_bank' | 'to_garage';

function rows<T>(res: { data: unknown; error: { message: string } | null }): T[] {
  if (res.error) throw new Refusal('SERVER_ERROR');
  return (res.data ?? []) as T[];
}

export const CASH = ['cash'] as const;

export async function loadBusinessCash(): Promise<BusinessCash | null> {
  return rows<BusinessCash>(await supabase.from('v_business_cash').select('*'))[0] ?? null;
}
const PLACE_ORDER = { garage: 0, bank: 1, round: 2 } as const;
/** Garage, bank, then the trucks' rounds (P5), as in the approved mockup. */
export async function loadCashPlaces(): Promise<CashPlace[]> {
  const list = rows<CashPlace>(await supabase.from('v_cash_by_place').select('*').order('name'));
  return list.sort((a, b) => PLACE_ORDER[a.kind] - PLACE_ORDER[b.kind]);
}
export async function loadCapitalHistory(): Promise<CapitalMove[]> {
  return rows<CapitalMove>(
    await supabase
      .from('v_capital_history')
      .select('*')
      .order('business_date', { ascending: false })
      .order('at', { ascending: false })
      .limit(200),
  );
}

export const capitalContribution = (amount: number, contributor: string, date: string, note: string) =>
  sendCommand('capital_contribution', {
    p_amount: amount,
    p_contributor: contributor,
    p_date: date,
    p_note: note || null,
  });
export const ownerWithdrawal = (amount: number, recipient: string, reason: string, date: string) =>
  sendCommand('owner_withdrawal', { p_amount: amount, p_recipient: recipient, p_reason: reason, p_date: date });
export const bankTransfer = (direction: Direction, amount: number, reference: string, date: string) =>
  sendCommand('bank_transfer', {
    p_direction: direction,
    p_amount: amount,
    p_reference: reference || null,
    p_date: date,
  });
export const postOpeningCash = (garage: number, bank: number) =>
  sendCommand('post_opening_cash', { p_garage: garage, p_bank: bank });
